import axios from "axios";
import { API_URL } from "./baseUrl";
import TokenService from "./token-service";
import { getUserTypeID } from "./common";
import {
  applyPhoneToLoginUser,
  isLoginStaffUser,
  resolveRequired2faPhone,
} from "./loginPhone2fa";

export const OTP_CHANNEL_SMS = "sms";
export const OTP_CHANNEL_EMAIL = "email";

export function readUserDataFromStorage() {
  try {
    return JSON.parse(localStorage.getItem("USER_DATA") || "{}");
  } catch {
    return {};
  }
}

/** Default channel: phone OTP when an MFA phone exists, otherwise email. */
export function getDefaultOtpChannel(userData = readUserDataFromStorage()) {
  return resolveRequired2faPhone(userData) ? OTP_CHANNEL_SMS : OTP_CHANNEL_EMAIL;
}

/** Stored preference, falling back to default when unset. */
export function getStoredOtpChannel(userData = readUserDataFromStorage()) {
  const raw = String(
    userData?.otp_channel ?? userData?.otpChannel ?? userData?.two_factor_channel ?? "",
  )
    .trim()
    .toLowerCase();

  if (raw === "sms" || raw === "phone") return OTP_CHANNEL_SMS;
  if (raw === "email") return OTP_CHANNEL_EMAIL;
  return getDefaultOtpChannel(userData);
}

export function persistOtpChannelToUserData(userData, otpChannel, phone) {
  const next = applyPhoneToLoginUser(
    {
      ...(userData || {}),
      otp_channel: otpChannel,
      otpChannel: otpChannel,
    },
    phone,
  );
  localStorage.setItem("USER_DATA", JSON.stringify(next));
  return next;
}

function authHeaders() {
  const token = TokenService.getLocalAccessToken();
  if (!token) return {};
  return {
    "x-access-token": token,
    "user-type": getUserTypeID(),
    "user-id": localStorage.getItem("user_id") || "",
  };
}

/**
 * Save 2FA channel (+ phone when SMS is selected).
 * Staff → personal_phone; client → mfa_phone.
 */
export async function updateTwoFactorAuthPreference({
  otpChannel,
  phone,
  userData = readUserDataFromStorage(),
}) {
  const staff = isLoginStaffUser(userData);
  const channel = otpChannel === OTP_CHANNEL_SMS ? OTP_CHANNEL_SMS : OTP_CHANNEL_EMAIL;

  const body = {
    otp_channel: channel,
    two_factor_channel: channel,
  };

  if (staff) {
    body.employee_id = userData.employee_id;
  } else {
    body.client_id = userData.client_id;
  }

  if (channel === OTP_CHANNEL_SMS && phone) {
    if (staff) {
      body.personal_phone = phone;
    } else {
      body.mfa_phone = phone;
    }
  } else if (phone) {
    // Keep phone on file even when email OTP is selected.
    if (staff) {
      body.personal_phone = phone;
    } else {
      body.mfa_phone = phone;
    }
  }

  try {
    const response = await axios.post(
      `${API_URL}auth/update-2fa-preference`,
      body,
      {
        withCredentials: true,
        headers: authHeaders(),
      },
    );

    if (response.status === 200 && response.data?.status === 1) {
      const savedPhone =
        phone ||
        resolveRequired2faPhone(response.data?.data || userData) ||
        null;
      const next = persistOtpChannelToUserData(userData, channel, savedPhone);
      return {
        success: true,
        message: response.data?.message || "Two-factor authentication updated",
        userData: next,
      };
    }

    if (response?.data?.message) {
      return { success: false, message: response.data.message };
    }
  } catch (error) {
    const status = error?.response?.status;
    if (status && status !== 404 && status !== 501) {
      return {
        success: false,
        message:
          error?.response?.data?.message ||
          "Failed to update two-factor authentication",
      };
    }
  }

  // Fallback: profile phone update + local preference until backend ships.
  if (phone) {
    const { CLIENT_ACTION, EMPLOYEE_ACTION } = await import("../redux/action");
    const userId = staff ? userData.employee_id : userData.client_id;

    const response = staff
      ? await EMPLOYEE_ACTION.insertUpdateEmployee({
          params: { personal_phone: phone },
          insertUpdateFlag: 1,
          employee_id: userId,
        })
      : await CLIENT_ACTION.insertUpdateClient({
          params: { mfa_phone: phone },
          insertUpdateFlag: 1,
          client_id: userId,
        });

    if (!(response?.status === 200 && response?.data?.status === 1)) {
      return {
        success: false,
        message:
          response?.data?.message ||
          "Failed to save phone number for two-factor authentication",
      };
    }
  }

  if (channel === OTP_CHANNEL_SMS && !phone && !resolveRequired2faPhone(userData)) {
    return {
      success: false,
      message: "Please enter a phone number to use phone OTP",
    };
  }

  const next = persistOtpChannelToUserData(
    userData,
    channel,
    phone || resolveRequired2faPhone(userData),
  );

  return {
    success: true,
    message:
      channel === OTP_CHANNEL_SMS
        ? "Two-factor authentication set to phone OTP"
        : "Two-factor authentication set to email OTP",
    userData: next,
    usedLocalFallback: true,
  };
}

/**
 * Fetch the latest 2FA channel preference from backend.
 * Source of truth for the UI should be this endpoint (not localStorage).
 */
export async function fetchTwoFactorAuthPreference() {
  try {
    const response = await axios.get(
      `${API_URL}auth/get-2fa-preference`,
      {
        withCredentials: true,
        headers: authHeaders(),
      },
    );

    // Be tolerant to different backend shapes.
    const payload = response?.data || {};
    if (payload?.status === 1 && payload?.data) {
      return { success: true, ...payload.data };
    }
    if (payload?.data && typeof payload.data === "object") {
      return { success: true, ...payload.data };
    }
    if (payload && typeof payload === "object") {
      return { success: true, ...payload };
    }
  } catch (error) {
    // Caller will fall back to default channel based on phone existence.
    return {
      success: false,
      message:
        error?.response?.data?.message ||
        "Failed to fetch 2FA preference",
    };
  }

  return { success: false, message: "2FA preference unavailable" };
}
